import { z } from 'zod';
import { tasksTaskCommentSchema, tasksTaskSchema, } from './tasks-task.contract.js';
export const tasksTaskDetailCapability = 'tasks.task-detail.v1';
export const tasksChecklistsCapability = 'tasks.checklists.v1';
export const tasksLinksCapability = 'tasks.links.v1';
export const tasksChecklistItemStatusSchema = z.enum(['open', 'done', 'removed']);
export const tasksTaskLinkHrefSchema = z.string().trim().min(1).max(2_048).refine((value) => {
    if (value.startsWith('/') && !value.startsWith('//'))
        return true;
    try {
        const url = new URL(value);
        return url.protocol === 'https:' || url.protocol === 'http:';
    }
    catch {
        return false;
    }
}, 'Task links must use an internal path or an HTTP(S) URL.');
export const tasksChecklistItemSchema = z.strictObject({
    id: z.string().uuid(),
    taskId: z.string().uuid(),
    templateInstanceId: z.string().uuid().nullable(),
    title: z.string().trim().min(1).max(500),
    required: z.boolean(),
    status: tasksChecklistItemStatusSchema,
    rank: z.number().int().nonnegative(),
    revision: z.number().int().positive(),
    createdByMembershipId: z.string().uuid(),
    completedByMembershipId: z.string().uuid().nullable(),
    completedAt: z.string().datetime({ offset: true }).nullable(),
    removedByMembershipId: z.string().uuid().nullable(),
    removedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const tasksTaskLinkSchema = z.strictObject({
    id: z.string().uuid(),
    taskId: z.string().uuid(),
    templateInstanceId: z.string().uuid().nullable(),
    label: z.string().trim().min(1).max(240),
    href: tasksTaskLinkHrefSchema,
    targetModuleId: z.string().regex(/^[a-z][a-z0-9-]*$/).nullable(),
    targetResourceType: z.string().regex(/^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/).nullable(),
    targetResourceId: z.string().max(240).nullable(),
    status: z.enum(['active', 'removed']),
    revision: z.number().int().positive(),
    addedByMembershipId: z.string().uuid(),
    removedByMembershipId: z.string().uuid().nullable(),
    removedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const tasksTemplateInstanceSchema = z.strictObject({
    id: z.string().uuid(),
    taskId: z.string().uuid(),
    templateId: z.string().regex(/^[a-z][a-z0-9-]{0,59}$/),
    configurationReleaseId: z.string().uuid().nullable(),
    templateHash: z.string().regex(/^[a-f0-9]{64}$/),
    checklistItemCount: z.number().int().nonnegative(),
    linkCount: z.number().int().nonnegative(),
    appliedByMembershipId: z.string().uuid(),
    appliedAt: z.string().datetime({ offset: true }),
});
export const tasksTaskDetailSchema = z.strictObject({
    task: tasksTaskSchema,
    detailRevision: z.number().int().positive(),
    comments: z.array(tasksTaskCommentSchema),
    checklistItems: z.array(tasksChecklistItemSchema),
    links: z.array(tasksTaskLinkSchema),
    templateInstances: z.array(tasksTemplateInstanceSchema),
    checklistProgress: z.strictObject({
        total: z.number().int().nonnegative(),
        completed: z.number().int().nonnegative(),
        required: z.number().int().nonnegative(),
        requiredCompleted: z.number().int().nonnegative(),
    }),
});
const detailCommandBase = {
    commandId: z.string().uuid(),
    expectedDetailRevision: z.number().int().positive(),
};
export const tasksAddChecklistItemCommandSchema = z.strictObject({
    ...detailCommandBase,
    kind: z.literal('add_checklist_item'),
    itemId: z.string().uuid(),
    title: z.string().trim().min(1).max(500),
    required: z.boolean().default(true),
    rank: z.number().int().nonnegative().default(0),
});
export const tasksUpdateChecklistItemCommandSchema = z.strictObject({
    ...detailCommandBase,
    kind: z.literal('update_checklist_item'),
    itemId: z.string().uuid(),
    expectedItemRevision: z.number().int().positive(),
    title: z.string().trim().min(1).max(500).optional(),
    required: z.boolean().optional(),
    rank: z.number().int().nonnegative().optional(),
}).superRefine((value, context) => {
    if (value.title === undefined && value.required === undefined && value.rank === undefined) {
        context.addIssue({ code: 'custom', message: 'At least one checklist field must change.' });
    }
});
export const tasksCompleteChecklistItemCommandSchema = z.strictObject({
    ...detailCommandBase,
    kind: z.literal('complete_checklist_item'),
    itemId: z.string().uuid(),
    expectedItemRevision: z.number().int().positive(),
    reason: z.string().trim().min(5).max(500).optional(),
});
export const tasksReopenChecklistItemCommandSchema = z.strictObject({
    ...detailCommandBase,
    kind: z.literal('reopen_checklist_item'),
    itemId: z.string().uuid(),
    expectedItemRevision: z.number().int().positive(),
    reason: z.string().trim().min(5).max(500).optional(),
});
export const tasksRemoveChecklistItemCommandSchema = z.strictObject({
    ...detailCommandBase,
    kind: z.literal('remove_checklist_item'),
    itemId: z.string().uuid(),
    expectedItemRevision: z.number().int().positive(),
    reason: z.string().trim().min(5).max(500),
});
export const tasksAddLinkCommandSchema = z.strictObject({
    ...detailCommandBase,
    kind: z.literal('add_link'),
    linkId: z.string().uuid(),
    label: z.string().trim().min(1).max(240),
    href: tasksTaskLinkHrefSchema,
    targetModuleId: z.string().regex(/^[a-z][a-z0-9-]*$/).nullable().default(null),
    targetResourceType: z.string().regex(/^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/).nullable().default(null),
    targetResourceId: z.string().trim().min(1).max(240).nullable().default(null),
}).superRefine((value, context) => {
    const targetParts = [value.targetModuleId, value.targetResourceType, value.targetResourceId];
    if (targetParts.some((part) => part !== null) && targetParts.some((part) => part === null)) {
        context.addIssue({ code: 'custom', message: 'Structured task link targets require every target field.' });
    }
});
export const tasksRemoveLinkCommandSchema = z.strictObject({
    ...detailCommandBase,
    kind: z.literal('remove_link'),
    linkId: z.string().uuid(),
    expectedLinkRevision: z.number().int().positive(),
    reason: z.string().trim().min(5).max(500),
});
export const tasksApplyTemplateCommandSchema = z.strictObject({
    ...detailCommandBase,
    kind: z.literal('apply_template'),
    templateInstanceId: z.string().uuid(),
    templateId: z.string().regex(/^[a-z][a-z0-9-]{0,59}$/),
});
export const tasksDetailCommandSchema = z.discriminatedUnion('kind', [
    tasksAddChecklistItemCommandSchema,
    tasksUpdateChecklistItemCommandSchema,
    tasksCompleteChecklistItemCommandSchema,
    tasksReopenChecklistItemCommandSchema,
    tasksRemoveChecklistItemCommandSchema,
    tasksAddLinkCommandSchema,
    tasksRemoveLinkCommandSchema,
    tasksApplyTemplateCommandSchema,
]);
export const tasksTaskDetailResponseSchema = z.strictObject({ data: tasksTaskDetailSchema });
export const tasksDetailCommandResponseSchema = z.strictObject({
    data: tasksTaskDetailSchema,
    idempotent: z.boolean(),
});
//# sourceMappingURL=tasks-detail.contract.js.map